import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { loadRecaptchaScript } from "./grecaptcha-loader";
import { useLoginConfig } from "../login-config-context.tsx";

declare global {
  interface Window {
    grecaptcha: ReCaptchaV2.ReCaptcha;
  }
}

interface InvisibleGoogleCaptchaRef {
  execute: () => Promise<string | null>;
  reset: () => void;
}

const InvisibleGoogleCaptcha = forwardRef<InvisibleGoogleCaptchaRef, unknown>((_, ref) => {
  const loginConfig = useLoginConfig();
  // ref to hold the resolve function of the captcha promise
  const captchaPromiseResolve = useRef<((value: string | null | PromiseLike<string | null>) => void) | null>(null);
  // ref to hold the promise that loads the recaptcha script
  const captchaLoadingPromise = useRef<Promise<void> | null>(null);
  // Function to execute the captcha, depends on captcha script being loaded
  const execute = () => {
    if (captchaLoadingPromise.current === null) {
      throw new Error("Captcha script didn't start loading. Did you forget to call loadRecaptchaScript?");
    }
    return captchaLoadingPromise.current.then(() => {
      return new Promise<string | null>((resolve) => {
        captchaPromiseResolve.current = resolve;
        if (!loginConfig) {
          // return null if login config is not available
          // skips the captcha challenge
          resolve(Promise.resolve(null));
        } else {
          void window.grecaptcha.execute();
        }
      });
    });
  };

  const reset = () => {
    if (!loginConfig) return;
    if (captchaLoadingPromise.current === null) {
      throw new Error("Captcha script didn't start loading. Did you forget to call loadRecaptchaScript?");
    }
    void captchaLoadingPromise.current.then(() => {
      window.grecaptcha.reset();
    });
    captchaPromiseResolve.current = null;
  };

  useImperativeHandle(ref, () => ({
    execute,
    reset,
  }));

  const captchaValidatedCallback = (token: string) => {
    if (captchaPromiseResolve.current) {
      captchaPromiseResolve.current(token);
    }
  };

  // 1. Use useEffect to wait for the component to be mounted
  // 2. Initiate script loading
  // 3. Wait for the script loading promise to resolve
  // 4. Once the script is loaded, render the captcha
  useEffect(() => {
    if (!loginConfig) {
      captchaLoadingPromise.current = Promise.resolve();
      return;
    }
    captchaLoadingPromise.current = loadRecaptchaScript();
    captchaLoadingPromise.current
      .then(() => {
        window.grecaptcha.ready(() => {
          window.grecaptcha.render("recaptcha-service", {
            sitekey: loginConfig.captchaSiteKey,
            size: "invisible",
            callback: captchaValidatedCallback,
          });
        });
      })
      .catch((error: unknown) => {
        console.error("Error loading captcha script", error);
      });
  }, []);

  // do not render or use the captcha if the login config is not available
  return loginConfig ? (
    <div key="recaptcha-service" id="recaptcha-service" className="g-recaptcha" style={{ display: "none" }} />
  ) : null;
});

export { InvisibleGoogleCaptcha, type InvisibleGoogleCaptchaRef };
